/**
 * RTL direction helper hook.
 *
 * Returns `{ isRTL, dir }` for the surrounding tree. Direction is a channel setting of its own
 * rather than a consequence of the language (CAIV-728): clubs with Arabic-speaking visitors ask for
 * the mirrored chat while keeping the bot on English, so the widget publishes the resolved value
 * through `DirectionContext` and every component reads it from there.
 *
 * Without a provider the language decides, which is what the inbox and the widget's own tests rely
 * on. Arabic is the only RTL language we ship.
 */

import { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

export const RTL_LANGUAGES = new Set(['ar']);

export type Direction = 'ltr' | 'rtl';

/** The direction a language reads in, used when nothing has pinned one. */
export function directionForLanguage(language: string | undefined | null): Direction {
  return RTL_LANGUAGES.has(language || '') ? 'rtl' : 'ltr';
}

/** Set by the widget root; `null` means "no one has decided, follow the language". */
export const DirectionContext = createContext<Direction | null>(null);

export function useDirection() {
  const { i18n } = useTranslation();
  const provided = useContext(DirectionContext);
  const dir = provided ?? directionForLanguage(i18n.language);
  return { isRTL: dir === 'rtl', dir };
}
